import type { TreeDto } from "api/types";
import { useTranslation } from "react-i18next";
import { getTreeViewPath } from "routes/Paths";
import { getTreeShareCardUrl } from "utilities";

import { useBrand } from "./useBrand";
import { SITE_ORIGIN, useSeo } from "./useSeo";

/**
 * Search and social metadata for a single tree, shared by the tree view and full-view pages.
 *
 * Both pages canonicalise to `/trees/:id` so the two routes are not competing copies of the same
 * content. Only a loaded, public tree is indexable: while the tree is still loading, or when it turns
 * out to be private, draft or unreachable, the page is marked noindex — a "this tree is private"
 * notice must never end up in search results, and a tree's title must not leak through a meta tag
 * to a visitor who cannot open it.
 *
 * Nothing about a non-public tree reaches the share tags. `useSeo` holds a noindex page at the generic
 * site card, and the cover image is withheld here as well so a private tree's photo is never handed to
 * anything that renders a link preview.
 */
export const useTreeSeo = (tree: TreeDto | null, treeId: string | undefined): void => {
	const { t } = useTranslation("seo");
	const { brand } = useBrand();

	const isPublic = tree?.visibility === "Public";
	const owner = tree?.ownerName?.trim() || "";

	const description = tree?.description?.trim()
		? tree.description.trim()
		: t(tree && tree.entityCount > 0 ? "tree.descriptionWithCount" : "tree.descriptionEmpty", {
				brand,
				count: tree?.entityCount ?? 0,
				owner
		  });

	useSeo({
		title: tree?.title,
		description: isPublic ? description : null,
		canonicalPath: treeId ? getTreeViewPath(treeId) : null,
		// The rendered card rather than the raw cover: it is 1200x630, which is what a large summary card
		// expects, and it carries the title and byline so a tree with no cover still previews as itself.
		imageUrl: isPublic && tree ? getTreeShareCardUrl(tree.id) : null,
		type: "article",
		noIndex: !isPublic,
		jsonLd:
			isPublic && tree
				? {
						"@context": "https://schema.org",
						"@type": "CreativeWork",
						name: tree.title,
						description,
						url: `${SITE_ORIGIN}${getTreeViewPath(tree.id)}`,
						...(tree.coverImageUrl ? { image: tree.coverImageUrl } : {}),
						...(owner ? { author: { "@type": "Person", name: owner } } : {}),
						dateCreated: tree.createdDate,
						dateModified: tree.lastModifiedDate,
						isAccessibleForFree: true
				  }
				: null
	});
};
